/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */

import { Component, EventEmitter, Input, Output, Inject, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';

import { AIAgentService } from '../../services/ai-agent.service'; // Adjust the import path as needed
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface ChatMessage {
  id: string;
  type: 'bot' | 'user';
  timestamp: Date;
  content: any;
}

@Component({
    selector: 'app-chat-history-list',
    templateUrl: './chat-history-list.component.html',
    styleUrls: ['./chat-history-list.component.scss'],
    standalone: false
})
export class ChatHistoryListComponent implements OnInit, OnDestroy, OnChanges {
    // @Input() chatHistoryList: any[] = [];
    @Input() showAIAgent: boolean = false;
    @Output() chatHistorySelected = new EventEmitter<any>();

    private destroy$ = new Subject<void>();
    messages: any[];
    private hasLoadedData = false;
    chatHistoryList: any[];
    
    // Infinite scroll properties
    private readonly initialHistoryCount: number = 10;
    private readonly itemsPerPage: number = 10;
    displayedCount: number = this.initialHistoryCount;
    isLoading: boolean = false;
    isLoadingHistory: boolean;

    constructor(public aiAgentService: AIAgentService) {}
    
    ngOnInit(): void {
        // Don't load data immediately - wait for showAIAgent to be true
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Check if showAIAgent changed and is now true
        if (changes.showAIAgent && changes.showAIAgent.currentValue === true && !this.hasLoadedData) {
            this.isLoadingHistory = true;
            this.getChatHistoryList();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Get the displayed history list based on current displayed count
     */
    get displayedHistoryList(): any[] {
        if (!this.chatHistoryList || this.chatHistoryList.length === 0) {
            return [];
        }
        
        return this.chatHistoryList.slice(0, this.displayedCount);
    }

    /**
     * Check if there are more items to show
     */
    get hasMoreItems(): boolean {
        return this.chatHistoryList && this.displayedCount < this.chatHistoryList.length;
    }

    /**
     * Handle infinite scroll - load more items when scrolled to bottom
     */
    onScroll(event: Event): void {
        const element = event.target as HTMLElement;
        const threshold = 50; // Load more when 50px from bottom
        
        if (element.scrollTop + element.scrollHeight >= element.scrollHeight - threshold && 
            this.hasMoreItems && 
            !this.isLoading) {
            this.loadMoreItems();
        }
    }

    /**
     * Load more chat history items
     */
    private loadMoreItems(): void {
        if (this.isLoading || !this.hasMoreItems) {
            return;
        }

        this.isLoading = true;
        
        // Simulate loading delay
        setTimeout(() => {
            this.displayedCount = Math.min(
                this.displayedCount + this.itemsPerPage,
                this.chatHistoryList.length
            );
            this.isLoading = false;
        }, 300);
    }

    /**
     * Get chat history from the service
     */
    getChatHistoryList(): void {
        this.aiAgentService.getConversationHistoryList()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (historyList) => {
                    // Process the history list with display properties and filter out items with null titles
                    this.chatHistoryList = (historyList || [])
                        .filter(item => item.Title != null && item.Title !== '') // Remove items with null, undefined, or empty titles
                        .map(item => ({
                            ...item,
                            displayDate: this.formatDate(item.lastMessageOn)
                        }));
                    
                    this.displayedCount = Math.min(this.initialHistoryCount, this.chatHistoryList.length);
                    
                    this.hasLoadedData = true;
                    this.isLoadingHistory = false;
                },
                error: (error) => {
                    console.error('Error fetching chat history in chat-history-list:', error);
                    this.isLoadingHistory = false;
                    this.chatHistoryList = []; // Reset on error
                    this.hasLoadedData = true; // Mark as loaded even on error to prevent retry
                }
            });
    }

    /**
     * Generate a unique ID for chat messages
     */
    private generateId(): string {
        return Math.random().toString(36).substr(2, 9) + Date.now();
    }

    /**
     * Parse the AI assistant's response content
     */
    private parseResponse(content: any): any {
        // If the content is already structured, return as is
        if (typeof content === 'object' && content !== null) {
            return content;
        }
        // Otherwise, wrap as plain text
        return {
            type: 'text',
            data: content || ''
        };
    }

    /**
     * Scroll the chat view to the bottom
     */
    private scrollToBottom(): void {
        // Implementation depends on the template.
        // This is a placeholder for actual scrolling logic.
        // For example, you might use ViewChild to get the chat container and scroll it.
    }



    /**
     * Load a specific chat history
     */
    loadChatHistory(historyItem: any): void {
        this.chatHistorySelected.emit(historyItem);
    }
 
    /**
     * Get the content of the last message for display in history title
     */
    getLastMessageContent(historyItem: any): string {
        if (!historyItem?.messages || historyItem.messages.length === 0) {
            return 'Empty conversation';
        }
       
        const lastMessage = historyItem.messages[historyItem.messages.length - 1];
        let content = lastMessage?.content || 'No content';
       
        // Remove the siteId string pattern using regex
        // Pattern matches: "siteId selected by user is [any number]"
        content = content.replace(/siteId selected by user is \d+/gi, '').trim();
       
        // If content is empty after removing siteId string, show fallback
        if (!content) {
            content = 'Previous conversation';
        }
       
        // Truncate long messages for display
        return content.length > 60 ? content.substring(0, 60) + '...' : content;
    }
 
    /**
     * Track function for chat history ngFor
     */
    trackByChatId(index: number, item: any): string {
        return item.id || item.chatId || index.toString();
    }
 
    /**
     * Format date for display
     */
    formatDate(date: any): string {
        if (!date) return '';
       
        const chatDate = new Date(date);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - chatDate.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
       
        if (diffDays === 0) {
            return 'Today';
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays >= 2 && diffDays <= 30) {
            return `Previous ${diffDays} Days`;
        } else {
            // For dates older than 30 days, show month name and year
            return chatDate.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
            });
        }
    }
 
    /**
     * Handle chat history item selection from the chat-history-list component
     */
    onChatHistoryItemSelected(historyItem: any): void {
        // Extract the ID and emit it to the parent (ai-agent-bot)
        const chatId = historyItem.id || historyItem.chatId;
        if (chatId) {
            this.chatHistorySelected.emit(historyItem);
        }
    }
 
}